/** @format */

import { styled } from "@mui/material/styles";
import { Box, Button, Chip, Typography } from "@mui/material";
import { colors } from "../../utility/types/constant";

// ── Page wrapper ──────────────────────────────────────────────────────────
export const SandboxPageWrapper = styled("div")(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#f8f9fa",
    paddingBottom: "60px",
}));

// ── Hero banner at top of sandbox page ───────────────────────────────────
export const SandboxHeroBanner = styled("div")(({ theme }) => ({
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
    padding: "60px 24px 48px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        padding: "48px 20px 36px",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "36px 16px 28px",
    },
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
            "radial-gradient(ellipse at 60% 40%, rgba(30,193,149,0.18) 0%, transparent 60%)",
        pointerEvents: "none",
    },
}));

export const SandboxBadge = styled(Chip)(({ theme }) => ({
    backgroundColor: "rgba(30, 193, 149, 0.2)",
    color: colors.mainSecondary,
    border: `1px solid rgba(30,193,149,0.4)`,
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "0.75rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "16px",
}));

export const SandboxHeroTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)",
    fontWeight: 800,
    color: "#ffffff",
    marginBottom: "12px",
    letterSpacing: "-0.02em",
}));

export const SandboxHeroSubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(0.9rem, 1.8vw, 1.1rem)",
    fontWeight: 400,
    color: "rgba(255,255,255,0.75)",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: 1.7,
}));

// ── Content area ──────────────────────────────────────────────────────────
export const SandboxContentArea = styled("div")(({ theme }) => ({
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "40px 24px 0",
    [theme.breakpoints.down("md")]: {
        padding: "32px 16px 0",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "20px 12px 0",
    },
}));

// ── Stat cards row ────────────────────────────────────────────────────────
export const SandboxStatCard = styled("div")(({ theme }) => ({
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "20px 24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    border: "1px solid rgba(0,0,0,0.05)",
    textAlign: "center",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
        padding: "14px 12px",
        borderRadius: "10px",
    },
}));

export const SandboxStatValue = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.1rem, 4vw, 1.75rem)",
    fontWeight: 800,
    color: colors.primary,
    lineHeight: 1.1,
    marginBottom: "4px",
}));

export const SandboxStatLabel = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#6c757d",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
}));

// ── Section card ──────────────────────────────────────────────────────────
export const SandboxSectionCard = styled("div")(({ theme }) => ({
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    border: "1px solid rgba(0,0,0,0.05)",
    marginBottom: "24px",
    [theme.breakpoints.down("sm")]: {
        padding: "16px 14px",
        borderRadius: "12px",
        marginBottom: "16px",
    },
}));

export const SandboxSectionTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "1rem",
    fontWeight: 700,
    color: colors.primary,
    marginBottom: "16px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
}));

// ── Member row ────────────────────────────────────────────────────────────
export const MemberRow = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 0",
    borderBottom: "1px solid #f1f3f4",
    gap: "12px",
    flexWrap: "wrap",
    "&:last-child": {
        borderBottom: "none",
    },
}));

export const MemberInfo = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
}));

export const MemberAvatar = styled("div")<{ isLeader?: boolean }>(
    ({ isLeader }) => ({
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        background: isLeader
            ? `linear-gradient(135deg, ${colors.mainSecondary} 0%, #143AA2 100%)`
            : `linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isLeader ? "#fff" : colors.primary,
        fontWeight: 700,
        fontSize: "0.875rem",
        fontFamily: "'Montserrat', sans-serif",
        flexShrink: 0,
    }),
);

export const MemberName = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.9375rem",
    fontWeight: 600,
    color: "#2d3748",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}));

export const MemberSubtext = styled(Typography)(() => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.75rem",
    color: "#6c757d",
}));

// ── Round row ─────────────────────────────────────────────────────────────
export const RoundRow = styled("div")<{ isNextUp?: boolean }>(({ theme, isNextUp }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderRadius: "10px",
    marginBottom: "10px",
    background: isNextUp
        ? `linear-gradient(135deg, rgba(30,193,149,0.08) 0%, rgba(30,193,149,0.03) 100%)`
        : "#f8f9fa",
    border: isNextUp ? `1.5px solid rgba(30,193,149,0.35)` : "1px solid #edf2f7",
    gap: "12px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "12px 10px",
        gap: "8px",
    },
}));

export const RoundNumber = styled("div")(() => ({
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: colors.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.875rem",
    fontFamily: "'Montserrat', sans-serif",
    flexShrink: 0,
}));

// ── Status chip helpers ───────────────────────────────────────────────────
export const StatusChip = styled(Chip)<{ statustype: string }>(
    ({ statustype }) => {
        const map: Record<string, { bg: string; color: string }> = {
            completed: { bg: "#e6f9f0", color: "#007200" },
            COMPLETED: { bg: "#e6f9f0", color: "#007200" },
            overdue: { bg: "#fff4e5", color: "#b76e00" },
            pending: { bg: "#e8f4fd", color: "#0077b6" },
            missed: { bg: "#fce8e8", color: "#cc0000" },
            failed: { bg: "#fce8e8", color: "#cc0000" },
            UPCOMING: { bg: "#e8f4fd", color: "#0077b6" },
            DUE_TODAY: { bg: "#fff9e0", color: "#a07800" },
            MISSED: { bg: "#fce8e8", color: "#cc0000" },
        };
        const style = map[statustype] ?? { bg: "#f0f0f0", color: "#555" };
        return {
            backgroundColor: style.bg,
            color: style.color,
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "0.7rem",
            height: "22px",
            borderRadius: "6px",
            "& .MuiChip-label": { padding: "0 8px" },
        };
    },
);

// ── CTA section at the bottom ─────────────────────────────────────────────
export const SandboxCTASection = styled("div")(({ theme }) => ({
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
    borderRadius: "20px",
    padding: "48px 32px",
    textAlign: "center",
    marginTop: "40px",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
        padding: "32px 16px",
        borderRadius: "14px",
        marginTop: "24px",
    },
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
            "radial-gradient(ellipse at 70% 30%, rgba(30,193,149,0.2) 0%, transparent 60%)",
        pointerEvents: "none",
    },
}));

export const CTATitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.3rem, 2.5vw, 1.9rem)",
    fontWeight: 800,
    color: "#ffffff",
    marginBottom: "10px",
    letterSpacing: "-0.01em",
}));

export const CTASubtitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
    fontWeight: 400,
    color: "rgba(255,255,255,0.75)",
    maxWidth: "500px",
    margin: "0 auto 28px",
    lineHeight: 1.7,
}));

export const CTAButton = styled(Button)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "1rem",
    padding: "14px 36px",
    borderRadius: "50px",
    backgroundColor: colors.mainSecondary,
    color: "#ffffff",
    textTransform: "none",
    boxShadow: "0 4px 20px rgba(30,193,149,0.4)",
    transition: "all 0.2s ease",
    "&:hover": {
        backgroundColor: "#17a37d",
        boxShadow: "0 6px 28px rgba(30,193,149,0.55)",
        transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        padding: "12px 24px",
    },
}));

export const CTASecondaryButton = styled(Button)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    fontSize: "0.95rem",
    padding: "12px 28px",
    borderRadius: "50px",
    border: "2px solid rgba(255,255,255,0.5)",
    color: "#ffffff",
    textTransform: "none",
    transition: "all 0.2s ease",
    "&:hover": {
        border: "2px solid rgba(255,255,255,0.85)",
        backgroundColor: "rgba(255,255,255,0.08)",
    },
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        padding: "10px 24px",
    },
}));

// ── Info disclaimer pill ──────────────────────────────────────────────────
export const SandboxDisclaimer = styled(Box)(({ theme }) => ({
    backgroundColor: "rgba(30,193,149,0.08)",
    border: "1px solid rgba(30,193,149,0.25)",
    borderRadius: "10px",
    padding: "12px 16px",
    marginBottom: "32px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
}));

// ── Next-up indicator label ───────────────────────────────────────────────
export const NextUpBadge = styled("span")(() => ({
    display: "inline-block",
    backgroundColor: colors.mainSecondary,
    color: "#fff",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: "0.65rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    padding: "2px 8px",
    borderRadius: "4px",
    marginLeft: "6px",
    verticalAlign: "middle",
}));
